import { safeFetchJSON } from '@charitystack/shared';
import { getAuthToken } from '../utils/auth';
import { decodeJwtPayloadSafe } from '../utils/authDiagnostics';

const writes = new Map();
const isObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

export function normalizeChecklistVisibility(value) {
  if (!isObject(value) || typeof value.dismissed !== 'boolean' || typeof value.reopened !== 'boolean') return null;
  if (value.dismissed && value.reopened) return null;
  return { dismissed: value.dismissed, reopened: value.reopened };
}

async function requestUser(userId, options = {}) {
  const base = import.meta.env.VITE_APP_ACCOUNTS_BACKEND;
  if (!base) throw new Error('Account preferences are unavailable');
  const token = await getAuthToken();
  // A queued preference save must never use a different signed-in user's token.
  if (decodeJwtPayloadSafe(token)?.sub !== userId) throw new Error('Account changed');
  const result = await safeFetchJSON(`${base.replace(/\/$/, '')}/users/me`, {
    ...options,
    headers: { Authorization: token },
    signal: AbortSignal.timeout(15000),
  }, { retries: 0, context: 'Checklist account preferences' });
  if (!result.ok) throw new Error('Could not access account preferences');
  const data = result.data;
  if (data?.userId !== userId || (data.preferences !== undefined && !isObject(data.preferences))) {
    throw new Error('Invalid account preferences');
  }
  return data.preferences || {};
}

function readVisibility(preferences, organizationId) {
  const byOrg = preferences.setupChecklistByOrg;
  if (byOrg === undefined) return null;
  if (!isObject(byOrg)) throw new Error('Invalid checklist preferences');
  if (!Object.hasOwn(byOrg, organizationId)) return null;
  const visibility = normalizeChecklistVisibility(byOrg[organizationId]);
  if (!visibility) throw new Error('Invalid checklist visibility');
  return visibility;
}

export async function loadChecklistVisibility(userId, organizationId) {
  return readVisibility(await requestUser(userId), organizationId);
}

export function saveChecklistVisibility(userId, organizationId, visibility, { initializeOnly = false } = {}) {
  const next = normalizeChecklistVisibility(visibility);
  if (!next) return Promise.reject(new Error('Invalid checklist visibility'));
  // Serialize this user's actions within this app, including organization switches.
  const previous = writes.get(userId) || Promise.resolve();
  const write = previous.catch(() => {}).then(async () => {
    // The existing API replaces preferences. Every save (including a retry) starts
    // with a fresh read so it retains unrelated settings and other organizations.
    const preferences = await requestUser(userId);
    const existing = readVisibility(preferences, organizationId);
    if (initializeOnly && existing) return existing;
    if (existing?.dismissed === next.dismissed && existing?.reopened === next.reopened) return existing;
    const updated = await requestUser(userId, {
      method: 'PATCH',
      body: JSON.stringify({ preferences: {
        ...preferences,
        setupChecklistByOrg: { ...preferences.setupChecklistByOrg, [organizationId]: next },
      } }),
    });
    const saved = readVisibility(updated, organizationId);
    if (saved?.dismissed !== next.dismissed || saved?.reopened !== next.reopened) {
      throw new Error('Checklist preference was not saved');
    }
    return saved;
  });
  writes.set(userId, write);
  const clear = () => { if (writes.get(userId) === write) writes.delete(userId); };
  write.then(clear, clear);
  return write;
}
